import type { ReactNode } from 'react';
import track from 'react-tracking';
import RawData from './components/RawData/RawData';
import Help from './components/Help/Help';
import TableSolid from '@simplywallst/ui-core/icons/TableSolid';
import More from './components/More';
import type { State as CompanyState } from '@/pages/CompanyReport/redux/interface';
import { getSummary } from '@/pages/CompanyReport/redux/selectors';
import { shallowEqual, useSelector } from 'react-redux';
import { useUser } from '@/hooks/useUser';
import { useOverviewTranslation } from '@/pages/CompanyReport/components/OverviewSection/hooks/useOverviewTranslation';
import { Button } from '@simplywallst/ui-core/next';

interface ReduxState {
  company: CompanyState;
}

function selector(state: ReduxState) {
  const summary = getSummary(state, {});
  return {
    companyId: summary.companyId,
    uniqueSymbol: summary.uniqueSymbol,
  };
}

export interface ChartActionsProps {
  id?: string;
  companyName?: string;
  helpTitle?: string;
  help?: ReactNode;
  rawData?: ReactNode;
  rawDataTitle?: ReactNode;
  feedbackLabel?: string;
  exportLightUrl?: string;
  exportDarkUrl?: string | null;
  actionsMarginTop?: number;
  moreActions?: ReactNode;
  toggleDataActive?: boolean;
  showDataButton: boolean;
  onToggleData?: () => void;
  shouldShowRegoModal: boolean;
}

function ChartActions(props: ChartActionsProps) {
  const { data: userData } = useUser();
  const selectedState = useSelector<
    Parameters<typeof selector>[0],
    ReturnType<typeof selector>
  >(selector, shallowEqual);
  const {
    rawData,
    rawDataTitle,
    help,
    helpTitle,
    exportLightUrl,
    exportDarkUrl,
    actionsMarginTop,
    moreActions,
    toggleDataActive,
    showDataButton,
    onToggleData,
    shouldShowRegoModal,
  } = props;
  const id = props.id || selectedState.companyId;
  const companyId = selectedState.companyId;
  const companyName = props.companyName || selectedState.uniqueSymbol;
  const feedbackLabel = props.feedbackLabel || '';

  const t = useOverviewTranslation();

  return (
    <div
      className="mt-x3 flex min-w-x20 items-center justify-end gap-x0_5"
      style={
        actionsMarginTop !== undefined && actionsMarginTop !== null
          ? { marginTop: `${actionsMarginTop}px` }
          : {}
      }
    >
      {moreActions}
      {showDataButton && (
        <Button
          data-cy-id={`chart-action-toggle-data-${id}`}
          size="medium"
          variant="outline"
          className={
            toggleDataActive
              ? 'border-[--s-button-outline-hover-color-bg] bg-[--s-button-outline-hover-color-bg]'
              : ''
          }
          onClick={onToggleData}
        >
          <TableSolid />
          <span className="hidden whitespace-nowrap md:inline">
            {t('actions.data')}
          </span>
        </Button>
      )}
      {rawData && (
        <RawData
          cyId={`chart-action-raw-data-${id}`}
          companyId={companyId}
          title={rawDataTitle}
          shouldShowRegoModal={shouldShowRegoModal}
        >
          {rawData}
        </RawData>
      )}
      {help && (
        <Help
          cyId={`chart-action-help-${id}`}
          companyId={companyId}
          title={helpTitle}
        >
          {help}
        </Help>
      )}
      {(feedbackLabel || exportLightUrl || exportDarkUrl) && (
        <More
          cyId={`chart-action-more-${id}`}
          userId={userData.id}
          companyName={companyName as string}
          feedbackLabel={feedbackLabel as string}
          exportLightUrl={exportLightUrl}
          exportDarkUrl={exportDarkUrl}
          shouldShowRegoModal={shouldShowRegoModal}
        />
      )}
    </div>
  );
}

export default track({ type: 'track' })(ChartActions) as typeof ChartActions;
